import { Refresh } from '@mui/icons-material'
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded'
import { Box, Button, Icon, Paper, Stack, Typography } from '@mui/material'
import * as Sentry from '@sentry/react'
import html2canvas from 'html2canvas'
import React, { useContext } from 'react'
import imageComponentFailed from 'src/assets/componentfailed.svg'
import { POST_HOG_BASE_URL } from 'src/assets/configs/globalConst'
import { AuthContext } from 'src/context/AuthContext'
import type { BugBannerType } from 'src/context/BugReportContext'
import { useBugReportContext } from 'src/context/BugReportContext'
import { lokiLogger } from 'src/services/lokiLogger'
import { metricsService } from 'src/services/metrices'
import { sendSlackNotification } from 'src/utils/api'
import { APP_VERSION } from 'src/utils/config/config'
import { getAccessLevel } from 'src/utils/functions/accessLevel'
import { getFrontendAlertChannel } from 'src/utils/functions/slackSupport'
import { forceClearCachesAndReload } from 'src/utils/sw/pwaUtils'

interface ErrorFallbackProps {
  error: Error
  resetError: () => void
  eventId: string | null
}
// checking vercel test
// Import DialogBugReport component to ensure it's rendered in the error boundary
import DialogBugReporting from 'src/pages/members/ReportBug/DialogBugReport'

// Create a wrapper component to use hooks and pass them down
const ErrorFallbackWrapper: React.FC<ErrorFallbackProps> = (props) => {
  // Get the bug report context here, in a function component where hooks are allowed
  const bugReportContext = useBugReportContext()

  // Pass both the original props and the bug report context to the actual component
  return (
    <>
      <ErrorFallbackContent
        {...props}
        bugReportContext={bugReportContext}
      />
      <DialogBugReporting />
    </>
  )
}

// The actual component implementation that receives the context as a prop
interface BugReportContextType {
  bannerOpen: BugBannerType
  setBannerOpen: React.Dispatch<React.SetStateAction<BugBannerType>>
}

const ErrorFallbackContent: React.FC<ErrorFallbackProps & { bugReportContext: BugReportContextType }> = ({ error, resetError, eventId, bugReportContext }) => {
  // Move all hook calls to the component body
  const { currentUser } = useContext(AuthContext)
  const { setBannerOpen } = bugReportContext

  const handleFeedback = () => {
    try {
      // Use the values from hooks that were called in the component body
      // const email = currentUser?.email || ''
      // const username = currentUser?.displayName || ''

      // Capture screenshot of the page
      const captureScreenshot = async () => {
        try {
          // Don't hide the error boundary - capture the entire screen including the error
          const pageElement = document.body // Capture entire body including error boundary
          if (!pageElement) {
            console.warn('Cannot find page element to capture')
            return
          }

          // Open the bug report dialog and show loading state
          setBannerOpen(() => ({
            open: true,
            data: {
              message: '',
              image: null,
              imageLoading: true
            }
          }))

          // Capture the screenshot with error boundary visible
          const canvas = await html2canvas(pageElement)
          const img = canvas.toDataURL('image/jpeg')

          if (img) {
            // Update the bug report dialog with the captured image
            setBannerOpen((prev) => ({
              ...prev,
              data: {
                message: error
                  ? `ERROR DETAILS\n\nType: ${error.name}\nMessage: ${error.message}\n\nStack Trace:\n${error.stack}\n\nEvent ID: ${eventId || 'Not available'}`
                  : '',
                image: img,
                imageLoading: false
              }
            }))
          } else {
            setBannerOpen((prev) => ({
              ...prev,
              data: {
                message: error ? `Error: ${error.message}\n\nStack: ${error.stack}` : '',
                image: null,
                imageLoading: false
              }
            }))
            console.error('Failed to capture screenshot')
          }
        } catch (err) {
          console.error('Error capturing screenshot:', err)
        }
      }

      // Start the screenshot capture process
      captureScreenshot()
    } catch (feedbackError) {
      console.error('Failed to open feedback dialog:', feedbackError)
    }
  }

  const handleReload = () => {
    forceClearCachesAndReload('sentry-error-boundary-refresh')
  }

  const handleExtendedLogging = () => {
    try {
      // Use currentUser from the hook that was already called at the component level
      // Fallback to sessionStorage 'data' key if context is not available
      let userData = null
      try {
        const sessionData = sessionStorage.getItem('data')
        userData = sessionData ? JSON.parse(sessionData) : null
      } catch (e) {
        // Silently handle parse errors
      }

      const email = currentUser?.email || userData?.email || 'unknown'
      const org = currentUser?.org || userData?.org || 'unknown'

      metricsService.trackError('GENERAL', 'not_applicable', 'unknown', 'Component load failed', window.location.pathname, {
        userType: process.env.NODE_ENV === 'production' ? 'external' : 'internal',
        duration: -1,
        version: APP_VERSION,
        pageUrl: window.location.pathname,
        apiUrl: 'not applicable',
        email: email,
        org: org,
        accessLevel: getAccessLevel(currentUser),
        methodName: 'not applicable'
      })

      // Log to Loki
      lokiLogger.logClientError(error, 'Component Load Failure', {
        componentName: 'ErrorFallback',
        eventId: eventId || 'N/A',
        user: email,
        org: org,
        accessLevel: getAccessLevel(currentUser),
        url: window.location.href
      })

      sendSlackNotification({
        message: `Component load failed for ${email} of ${org};`,
        channel: getFrontendAlertChannel(),
        title: `urls:
          \`<${window.location.href}|${window.location.href}>\`
          \`<${POST_HOG_BASE_URL}/person/${email}#activeTab=sessionRecordings|${email}>\`
          \`<${POST_HOG_BASE_URL}/groups/0/${org}|${org}>\`
          \`${error.message}\`
        `
      })
    } catch (logError) {
      console.error('Failed to send extended error logging:', logError)
    }
  }

  React.useEffect(() => {
    // Use UNIFIED asset error handler - single source of truth for all asset loading errors
    // This prevents multiple systems from competing and causing infinite loops
    import('src/utils/sw/pwaUtils').then(({ handleAssetLoadingError, isAssetLoadingError }) => {
      if (isAssetLoadingError(error)) {
        handleAssetLoadingError(error, 'SentryErrorBoundary').then((handled) => {
          if (!handled) {
            // Reload was already attempted recently - show error UI
            handleExtendedLogging()
          }
          // If handled=true, page is reloading - no action needed
        })
      } else {
        // Not an asset loading error - log and show error UI
        handleExtendedLogging()
      }
    }).catch(() => {
      // If import fails, fall back to showing error UI
      handleExtendedLogging()
    })
  }, [error])

  return (
    <Stack
      className="error-boundary-container"
      sx={{
        width: '100%',
        height: '100vh',
        position: 'relative',
        top: 0,
        left: 0,
        background: 'rgba(245, 245, 245, 0.95)',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 900
      }}>
      <Box
        sx={{
          width: { xs: '90%', sm: '80%', md: '650px' },
          maxWidth: '650px',
          position: 'relative'
        }}>
        <Paper
          elevation={4}
          sx={{
            backgroundColor: '#f8f9fa',
            padding: { xs: 3, sm: 4 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderRadius: 2,
            overflow: 'hidden'
          }}>
          <img
            src={imageComponentFailed}
            alt="Component Failed"
            style={{
              maxWidth: '180px',
              height: 'auto',
              marginBottom: '1.5rem'
            }}
          />
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{ mb: 1 }}>
            <Icon
              color="error"
              sx={{ fontSize: 28 }}>
              <ReportProblemRoundedIcon fontSize="inherit" />
            </Icon>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                fontSize: { xs: '1.5rem', sm: '2rem' }
              }}>
              Oops, Something went wrong
            </Typography>
          </Stack>

          {/* Content area */}
          <Box width="100%">
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mb: 3, textAlign: 'center' }}>
              We're sorry for the inconvenience. The error has been automatically reported to our team.
            </Typography>

            {/* Error details (development only) */}
            {process.env.NODE_ENV === 'development' && (
              <Paper
                elevation={0}
                sx={{
                  height: '150px',
                  p: 2.5,
                  mb: 3.5,
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  borderRadius: 2,
                  border: '1px solid rgba(0, 0, 0, 0.08)',
                  textAlign: 'left',
                  overflow: 'auto'
                }}>
                <Typography
                  variant="caption"
                  component="pre"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'monospace',
                    fontSize: '0.85rem',
                    color: 'error.main'
                  }}>
                  {error.message}
                  {'\n\n'}
                  {error.stack}
                </Typography>
              </Paper>
            )}

            {/* Action buttons */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              justifyContent="center"
              sx={{ mt: 2 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleReload}
                startIcon={<Refresh />}
                sx={{
                  fontSize: '14px',
                  py: 1,
                  px: 3,
                  borderRadius: 2,
                  boxShadow: 2
                }}>
                Refresh Page
              </Button>

              {eventId && (
                <Button
                  variant="outlined"
                  onClick={handleFeedback}
                  sx={{
                    fontSize: '14px',
                    py: 1,
                    px: 3,
                    borderRadius: 2
                  }}>
                  Report Feedback
                </Button>
              )}
            </Stack>

            {/* Error ID */}
            {eventId && (
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mt: 3.5,
                  textAlign: 'center',
                  color: 'text.secondary',
                  opacity: 0.8
                }}>
                Error ID: {eventId}
              </Typography>
            )}
          </Box>
        </Paper>
      </Box>
    </Stack>
  )
}

// Enhanced Error Boundary with Sentry integration
interface SentryErrorBoundaryState {
  hasError: boolean
  error: Error | null
  eventId: string | null
}

export class SentryErrorBoundary extends React.Component<{ children: React.ReactNode }, SentryErrorBoundaryState> {
  constructor(props: { children: React.ReactNode }) {
    super(props)
    this.state = { hasError: false, error: null, eventId: null }
  }

  static getDerivedStateFromError(error: Error): SentryErrorBoundaryState {
    return { hasError: true, error, eventId: null }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const eventId = Sentry.captureException(error)
    console.error('Error Boundary caught an error:', error, errorInfo)

    // Log to Loki
    lokiLogger.logClientError(error, errorInfo.componentStack ? 'React Error Boundary' : 'Unknown Component', {
      componentStack: errorInfo.componentStack,
      errorBoundary: true,
      eventId
    })

    this.setState({ eventId })
  }

  render() {
    if (this.state.hasError && this.state.error) {
      return (
        <ErrorFallbackWrapper
          error={this.state.error}
          resetError={() => this.setState({ hasError: false, error: null, eventId: null })}
          eventId={this.state.eventId}
        />
      )
    }

    return this.props.children
  }
}

// Higher-order component for wrapping components with error boundaries
export const withSentryErrorBoundary = <P extends object>(
  Component: React.ComponentType<P>,
  options?: {
    name?: string
    fallback?: React.ComponentType<ErrorFallbackProps>
  }
) => {
  const WrappedComponent = (props: P) => (
    <SentryErrorBoundary>
      <Component {...props} />
    </SentryErrorBoundary>
  )

  WrappedComponent.displayName = `withSentryErrorBoundary(${options?.name || Component.displayName || Component.name})`

  return WrappedComponent
}

export default SentryErrorBoundary
